import React, { useState, useEffect } from 'react';
import { UserCircleIcon, PlusIcon, SearchIcon, EyeIcon, EyeOffIcon, KeyIcon } from '@heroicons/react/outline';
import { motion } from 'framer-motion';

function ManageUsersTab({ user, setMessage }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', full_name: '', password: '', is_admin: false });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Password-change modal state (admin setting another user's password)
  const [pwUser, setPwUser] = useState(null); // the user whose password is being changed
  const [pwValue, setPwValue] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPwValue, setShowPwValue] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const loadUsers = () => {
    fetch('/api/user.php?action=admin_list_users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ search }),
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.users)) {
          setUsers(data.users);
          setError('');
        } else {
          setUsers([]);
          setError('Failed to load users');
        }
      })
      .catch(() => {
        setUsers([]);
        setError('Failed to load users');
      });
  };

  useEffect(() => {
    loadUsers();
  }, [search]);

  const createUser = e => {
    e.preventDefault();
    fetch('/api/user.php?action=admin_create_user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser),
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setShowCreate(false);
        setNewUser({ username: '', email: '', full_name: '', password: '', is_admin: false });
        loadUsers();
        setMessage('User created');
        setError('');
      })
      .catch(() => setError('Failed'));
  };

  const openPasswordModal = u => {
    setPwUser(u);
    setPwValue('');
    setPwConfirm('');
    setShowPwValue(false);
    setPwError('');
  };

  const closePasswordModal = () => {
    setPwUser(null);
    setPwValue('');
    setPwConfirm('');
    setShowPwValue(false);
    setPwError('');
    setPwSaving(false);
  };

  const savePassword = e => {
    e.preventDefault();
    if (pwValue.length < 8) {
      setPwError('New password must be at least 8 characters');
      return;
    }
    if (pwValue !== pwConfirm) {
      setPwError('Passwords do not match');
      return;
    }
    setPwSaving(true);
    fetch('/api/user.php?action=admin_reset_password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: pwUser.id, new_password: pwValue }),
      credentials: 'include'
    })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          throw new Error(data.error || 'Failed to change password');
        }
        return data;
      })
      .then(() => {
        setMessage(`Password updated for ${pwUser.username}`);
        closePasswordModal();
      })
      .catch(err => {
        setPwError(err.message);
        setPwSaving(false);
      });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-gray-800">
      {error && <div className="mb-2 text-red-600">{error}</div>}
      <div className="mb-4 flex justify-between items-center">
        <div className="relative">
          <SearchIcon className="w-5 h-5 text-gray-400 absolute left-2 top-2.5" />
          <input
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-gray-300 rounded-md pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition"
        >
          <PlusIcon className="w-5 h-5" />
          Create User
        </button>
      </div>
      <div className="overflow-x-auto bg-white rounded-md shadow">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2">Avatar</th>
              <th className="px-4 py-2">Full Name</th>
              <th className="px-4 py-2">Username</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t hover:bg-gray-50 transition">
                <td className="px-4 py-2">
                  {u.profile_picture_url ? (
                    <img src={u.profile_picture_url} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <UserCircleIcon className="w-8 h-8 text-gray-300" />
                  )}
                </td>
                <td className="px-4 py-2">{u.full_name}</td>
                <td className="px-4 py-2">{u.username}</td>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2">{u.is_admin ? 'Admin' : 'User'}</td>
                <td className="px-4 py-2">{u.created_at}</td>
                <td className="px-4 py-2">
                  {Number(u.id) === Number(user?.id) ? (
                    <span className="text-gray-400 text-xs">Use Profile tab</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPasswordModal(u)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition"
                    >
                      <KeyIcon className="w-4 h-4" />
                      Change Password
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 max-w-md bg-white rounded-md shadow p-4">
          <h3 className="font-semibold mb-2">Create User</h3>
          <form onSubmit={createUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium">Username</label>
              <input
                value={newUser.username}
                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Full Name</label>
              <input
                value={newUser.full_name}
                onChange={e => setNewUser({ ...newUser, full_name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_admin"
                checked={newUser.is_admin}
                onChange={e => setNewUser({ ...newUser, is_admin: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="is_admin">Admin</label>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Save
            </button>
          </form>
        </motion.div>
      )}

      {pwUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
          onClick={() => { if (!pwSaving) closePasswordModal(); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-md shadow-lg p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold mb-1">Change Password</h3>
            <p className="text-sm text-gray-500 mb-4">
              Set a new password for <span className="font-medium text-gray-700">{pwUser.username}</span>
              {pwUser.full_name ? ` (${pwUser.full_name})` : ''}.
            </p>
            {pwError && <div className="mb-2 text-red-600 text-sm">{pwError}</div>}
            <form onSubmit={savePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">New Password</label>
                <div className="relative">
                  <input
                    type={showPwValue ? 'text' : 'password'}
                    value={pwValue}
                    onChange={e => setPwValue(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                    autoComplete="new-password"
                    autoFocus
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwValue(prev => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showPwValue ? 'Hide password' : 'Show password'}
                  >
                    {showPwValue ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">Minimum 8 characters.</p>
              </div>
              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <input
                  type={showPwValue ? 'text' : 'password'}
                  value={pwConfirm}
                  onChange={e => setPwConfirm(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  disabled={pwSaving}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={pwSaving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-60"
                >
                  {pwSaving ? 'Saving…' : 'Update Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

export default ManageUsersTab;
